document.addEventListener('DOMContentLoaded', () => {
  const CART_DRAWER_SECTION_ID = 'ajax-cart-drawer';
  const CART_PAGE_SECTION_ID = 'main-cart';

  const getDrawer = () => document.getElementById('AjaxCartDrawer');
  const getDrawerContent = () => document.getElementById('CartDrawerContent');

  const openDrawer = () => {
    const drawer = getDrawer();
    const drawerContent = getDrawerContent();
    if (!drawer || !drawerContent) return;

    drawer.classList.remove('invisible', 'opacity-0');
    drawer.setAttribute('aria-hidden', 'false');
    drawerContent.classList.remove('translate-x-full');
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    const drawer = getDrawer();
    const drawerContent = getDrawerContent();
    if (!drawer || !drawerContent) return;

    drawer.classList.add('opacity-0');
    drawer.setAttribute('aria-hidden', 'true');
    drawerContent.classList.add('translate-x-full');
    window.setTimeout(() => {
      const latestDrawer = getDrawer();
      if (latestDrawer && latestDrawer.classList.contains('opacity-0')) {
        latestDrawer.classList.add('invisible');
      }
    }, 300);
    document.body.style.overflow = '';
  };

  const fetchSectionDocument = async (url) => {
    const response = await fetch(url, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch section markup from ${url}.`);
    }

    const markup = await response.text();
    return new DOMParser().parseFromString(markup, 'text/html');
  };

  const refreshCartDrawer = async () => {
    const drawer = getDrawer();
    if (!drawer) return;

    const documentFragment = await fetchSectionDocument(`/?section_id=${CART_DRAWER_SECTION_ID}`);
    const nextDrawer = documentFragment.getElementById('AjaxCartDrawer');

    if (nextDrawer) {
      drawer.innerHTML = nextDrawer.innerHTML;
    }
  };

  const refreshCartPage = async () => {
    const currentCartSection = document.querySelector('[data-main-cart-section]');
    if (!currentCartSection) return;

    const documentFragment = await fetchSectionDocument(`/cart?section_id=${CART_PAGE_SECTION_ID}`);
    const nextCartSection = documentFragment.querySelector('[data-main-cart-section]');

    if (nextCartSection) {
      currentCartSection.replaceWith(nextCartSection);
    }
  };

  const syncCartCount = async () => {
    const response = await fetch('/cart.js', {
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch cart state.');
    }

    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(cart.item_count);
    });
  };

  const refreshCartUI = async () => {
    await Promise.all([
      refreshCartDrawer(),
      refreshCartPage(),
      syncCartCount()
    ]);
  };

  const setButtonDisabledState = (button, disabled, label) => {
    if (!button) return;

    button.disabled = disabled;
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    button.classList.toggle('opacity-50', disabled);
    button.classList.toggle('cursor-not-allowed', disabled);
    button.classList.toggle('cursor-pointer', !disabled);
    button.classList.toggle('hover:brightness-95', !disabled);

    if (label) {
      button.textContent = label;
    }
  };

  const setFormSubmittingState = (form, submitting, redirectToCheckout, submitter) => {
    if (!form) return;

    const addToCartButton = form.querySelector('[data-add-to-cart-button]') || form.querySelector('button[type="submit"]');
    const buyNowButton = form.querySelector('[data-buy-now-button]');

    if (addToCartButton && !addToCartButton.dataset.originalLabel) {
      addToCartButton.dataset.originalLabel = addToCartButton.textContent.trim();
    }

    if (buyNowButton && !buyNowButton.dataset.originalLabel) {
      buyNowButton.dataset.originalLabel = buyNowButton.textContent.trim();
    }

    if (submitting) {
      setButtonDisabledState(
        addToCartButton,
        true,
        redirectToCheckout && submitter === buyNowButton
          ? (addToCartButton && addToCartButton.dataset.originalLabel) || 'Add to Cart'
          : 'Adding...'
      );
      setButtonDisabledState(
        buyNowButton,
        true,
        redirectToCheckout ? 'Redirecting...' : (buyNowButton && buyNowButton.dataset.originalLabel) || 'Buy Now'
      );
      return;
    }

    if (addToCartButton) {
      setButtonDisabledState(addToCartButton, false, addToCartButton.dataset.originalLabel || 'Add to Cart');
    }

    if (buyNowButton) {
      setButtonDisabledState(buyNowButton, false, buyNowButton.dataset.originalLabel || 'Buy Now');
    }
  };

  const submitAjaxCartForm = async (form, options = {}) => {
    if (!form || form.dataset.cartBusy === 'true') return;

    const redirectToCheckout = options.redirectToCheckout === true;
    const submitter = options.submitter || null;
    form.dataset.cartBusy = 'true';
    setFormSubmittingState(form, true, redirectToCheckout, submitter);

    try {
      const formData = new FormData(form);
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.description || 'Error adding item to cart.');
      }

      await refreshCartUI();

      if (redirectToCheckout) {
        window.location.href = '/checkout';
        return;
      }

      openDrawer();
    } catch (error) {
      console.error('Error adding to cart', error);
      setFormSubmittingState(form, false, redirectToCheckout, submitter);
      delete form.dataset.cartBusy;
      return;
    }

    setFormSubmittingState(form, false, redirectToCheckout, submitter);
    delete form.dataset.cartBusy;
  };

  const updateCartLine = async (line, quantity) => {
    const nextQuantity = Number.isFinite(quantity) ? quantity : parseInt(quantity, 10);
    if (!line || Number.isNaN(nextQuantity) || nextQuantity < 0) return;

    try {
      const response = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: parseInt(line, 10),
          quantity: nextQuantity
        })
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        throw new Error(errorPayload.description || 'Error updating cart.');
      }

      const drawerWasOpen = !!getDrawer() && !getDrawer().classList.contains('invisible');
      await refreshCartUI();

      if (drawerWasOpen) {
        openDrawer();
      }
    } catch (error) {
      console.error('Error updating cart', error);
    }
  };

  // Product Image Thumbnails
  document.querySelectorAll('[data-thumb-button]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const thumb = e.currentTarget;
      const productSection = thumb.closest('[data-product-section]');
      const url = thumb.dataset.imageUrl;
      const mainImg = productSection ? productSection.querySelector('[data-main-product-image]') : null;

      if (mainImg && url) {
        mainImg.removeAttribute('srcset');
        mainImg.src = url;
      }

      if (!productSection) return;

      productSection.querySelectorAll('[data-thumb-button]').forEach(item => {
        item.classList.remove('border-amazon-orange');
        item.classList.add('border-gray-300');
        item.removeAttribute('aria-current');
      });

      thumb.classList.remove('border-gray-300');
      thumb.classList.add('border-amazon-orange');
      thumb.setAttribute('aria-current', 'true');
    });
  });

  // Accordion Logic (moved to delegated click handler below)

  // Variant Change Logic (Price, Image, URL)
  document.querySelectorAll('[data-variant-select]').forEach(variantSelect => {
    variantSelect.addEventListener('change', (e) => {
      const select = e.currentTarget;
      const selectedOption = select.options[select.selectedIndex];
      const productSection = select.closest('[data-product-section]');

      if (!selectedOption || !productSection) return;

      const priceText = selectedOption.dataset.price || '';
      productSection.querySelectorAll('[data-product-price]').forEach(priceElement => {
        priceElement.innerText = priceText;
      });

      const compareAtPriceText = selectedOption.dataset.compareAtPrice || '';
      productSection.querySelectorAll('[data-product-compare-price]').forEach(compareElement => {
        compareElement.innerText = compareAtPriceText;
        compareElement.classList.toggle('hidden', !compareAtPriceText);
      });

      const available = selectedOption.dataset.available === 'true';
      const availabilityElement = productSection.querySelector('[data-product-availability]');
      if (availabilityElement) {
        availabilityElement.innerText = available
          ? productSection.dataset.inStockLabel
          : productSection.dataset.outOfStockLabel;
        availabilityElement.classList.toggle('text-green-700', available);
        availabilityElement.classList.toggle('text-red-600', !available);
      }

      setButtonDisabledState(
        productSection.querySelector('[data-add-to-cart-button]'),
        !available,
        available ? productSection.dataset.addToCartLabel : productSection.dataset.soldOutLabel
      );
      setButtonDisabledState(productSection.querySelector('[data-buy-now-button]'), !available);

      const variantId = selectedOption.value;
      if (variantId) {
        const url = new URL(window.location);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      }

      const imageUrl = selectedOption.dataset.imageUrl;
      const mainImg = productSection.querySelector('[data-main-product-image]');
      if (mainImg && imageUrl) {
        mainImg.removeAttribute('srcset');
        mainImg.src = imageUrl;
      }

      if (!imageUrl) return;

      productSection.querySelectorAll('[data-thumb-button]').forEach(thumb => {
        const isActive = thumb.dataset.imageUrl === imageUrl;
        thumb.classList.toggle('border-amazon-orange', isActive);
        thumb.classList.toggle('border-gray-300', !isActive);

        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    });
  });

  // Carousel Sliding Logic
  document.querySelectorAll('.group\\/carousel').forEach(carouselContainer => {
    const scrollArea = carouselContainer.querySelector('.overflow-x-auto');
    const prevBtn = carouselContainer.querySelector('button.left-0');
    const nextBtn = carouselContainer.querySelector('button.right-0');

    if (scrollArea && prevBtn && nextBtn) {
      prevBtn.addEventListener('click', () => {
        const scrollAmount = Math.max(scrollArea.clientWidth * 0.8, 320);
        scrollArea.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', () => {
        const scrollAmount = Math.max(scrollArea.clientWidth * 0.8, 320);
        scrollArea.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    }
  });

  document.addEventListener('click', (event) => {
    const accordionTrigger = event.target.closest('.js-accordion-trigger');
    if (accordionTrigger) {
      const targetId = accordionTrigger.dataset.target;
      const target = document.getElementById(targetId);
      const icon = accordionTrigger.querySelector('.js-accordion-icon');
      if (target && icon) {
        const isOpening = target.classList.contains('hidden');
        target.classList.toggle('hidden', !isOpening);
        accordionTrigger.setAttribute('aria-expanded', isOpening ? 'true' : 'false');
        icon.innerHTML = isOpening
          ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>'
          : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>';
      }
      return;
    }

    const drawerOpenButton = event.target.closest('.js-drawer-open-cart');
    if (drawerOpenButton) {
      event.preventDefault();
      openDrawer();
      return;
    }

    const drawerCloseButton = event.target.closest('.js-drawer-close');
    if (drawerCloseButton) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const buyNowButton = event.target.closest('[data-buy-now-button]');
    if (buyNowButton) {
      const form = buyNowButton.closest('form[data-product-form][data-ajax-cart]');
      if (!form || buyNowButton.disabled) return;

      event.preventDefault();
      form.dataset.buyNow = 'true';
      form._cartSubmitter = buyNowButton;
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      return;
    }

    const quantityButton = event.target.closest('.js-qty-btn');
    if (quantityButton) {
      const cartLineItem = quantityButton.closest('[data-cart-line-item]');
      const line = quantityButton.dataset.line || (cartLineItem && cartLineItem.dataset.line) || '';
      const input = cartLineItem ? cartLineItem.querySelector(`input[data-line="${line}"]`) : null;
      const currentQuantity = input ? parseInt(input.value, 10) || 0 : 0;
      const nextQuantity = quantityButton.dataset.action === 'plus' ? currentQuantity + 1 : currentQuantity - 1;

      event.preventDefault();
      if (input) {
        input.value = String(Math.max(nextQuantity, 0));
      }
      updateCartLine(line, Math.max(nextQuantity, 0));
      return;
    }

    const removeButton = event.target.closest('.js-remove-item');
    if (removeButton) {
      event.preventDefault();
      updateCartLine(removeButton.dataset.line, 0);
    }
  });

  document.addEventListener('change', (event) => {
    const quantityInput = event.target.closest('input[type="number"][data-line]');
    if (!quantityInput) return;

    const rawValue = parseInt(quantityInput.value, 10);
    const nextQuantity = Number.isNaN(rawValue) ? 1 : Math.max(rawValue, 0);
    quantityInput.value = String(nextQuantity);
    updateCartLine(quantityInput.dataset.line, nextQuantity);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;

    event.preventDefault();
    const redirectToCheckout = form.dataset.buyNow === 'true';
    delete form.dataset.buyNow;
    const submitter = form._cartSubmitter || event.submitter || null;
    delete form._cartSubmitter;
    submitAjaxCartForm(form, {
      redirectToCheckout,
      submitter
    });
  });
});
